import { useCallback, useContext, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';

import AccountPickerContext from 'contexts/AccountPickerContext';
import AnalyticsContext from 'contexts/AnalyticsContext';
import CurrentUserContext from 'contexts/CurrentUserContext';
import DashboardsContext from 'contexts/DashboardsContext';
import ToastContext from 'contexts/ToastContext';
import { PortalsContext } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider';
import { buildDashboardShow, buildReportShow } from 'navigation/appRoutes';
import buildCardLayout from 'contextProviders/WidgetGalleryProvider/buildCardLayout';
import findStartingY from 'contextProviders/WidgetGalleryProvider/findStartingY';
import createDashboardAlt from 'api/createDashboardAlt';
import createDashboardGadget from 'api/createDashboardGadget';
import updateDashboard from 'api/updateDashboard';
import withoutNulls from 'api/expression/withoutNulls';
import getIdentifier from 'getIdentifier';
import STORE from 'store';
import { InsightsChartSpec } from 'services/streamInsightsQuery';

import buildChartDefFromSpec from './buildChartDefFromSpec';
import buildScopeFromSource from './buildScopeFromSource';

// What the user changed while exploring the chart, when the save came from the
// Explore modal rather than straight from the chat bubble. Each field falls back
// to the value derived from the chat spec, so a plain chat save is unaffected.
export interface InsightsChartSaveOverrides {
  chartDef?: VisualisationDefinition;
  drillDowns?: FilterPlateType[];
  scope?: FilterPlateType[];
  dateField?: string;
  // The whole date block, not just dateRange: picking "Last month" in Explore
  // leaves dateRange undefined and puts the range in relativeDateRange, so
  // carrying only dateRange would silently drop it.
  dateRange?: DateRangeInput;
  relativeDateRange?: RelativeDateRange;
  advancedRelativeDateRange?: AdvancedRelativeDateRange;
}

// Persists a chat chart as a live gadget on a dashboard or report (new or
// existing), then toasts and navigates to it. Each save rebuilds a fresh
// V5ChartDefinition so the saved gadget re-queries data rather than freezing the
// chat's snapshot.
const useSaveInsightsChart = (
  spec: InsightsChartSpec,
  overrides?: InsightsChartSaveOverrides,
) => {
  const { selectedAccountId, accountRef } = useContext(AccountPickerContext);
  const { id: currentUserId } = useContext(CurrentUserContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const { addOrUpdateDashboard } = useContext(DashboardsContext);
  const { selectedPortal } = useContext(PortalsContext);
  const { showToast } = useContext(ToastContext);
  const navigate = useNavigate();

  const [isSaving, setIsSaving] = useState(false);

  // An explored chart carries its own date block (which may be relative); a
  // chat save pins the fixed range the user saw in the conversation.
  const isExplored = overrides !== undefined;
  const dateBlock = useMemo(
    () =>
      isExplored
        ? {
            dateField: overrides.dateField ?? 'date',
            dateRange: overrides.dateRange,
            relativeDateRange: overrides.relativeDateRange,
            advancedRelativeDateRange: overrides.advancedRelativeDateRange,
          }
        : {
            dateField: 'date',
            dateRange: {
              startDate: spec.source.dateScope.startDate,
              endDate: spec.source.dateScope.endDate,
            },
          },
    [isExplored, overrides, spec.source.dateScope],
  );

  // The chat's filters, as a persisted gadget scope, so the saved chart shows the
  // same filtered data the user saw.
  const scope = useMemo(
    () => overrides?.scope ?? buildScopeFromSource(spec.source.filters),
    [overrides, spec.source.filters],
  );

  // Build a fresh DashboardGadget/ReportGadget for one save, reproducing what
  // the user was looking at — in chat, or in the Explore modal after drilling
  // and reshaping. The dashboard's own controls can change it afterwards.
  const buildGadget = useCallback(
    (now: string): DashboardGadget => ({
      id: getIdentifier(),
      chartDef: overrides?.chartDef ?? buildChartDefFromSpec(spec),
      drillDowns: overrides?.drillDowns ?? [],
      scope,
      ...dateBlock,
      isAIGenerated: true,
      createdBy: currentUserId,
      createdOn: now,
      updatedBy: currentUserId,
      updatedOn: now,
    }),
    [spec, overrides, scope, dateBlock, currentUserId],
  );

  const finish = useCallback(
    ({ name, route }: { name: string; route: string }): boolean => {
      setIsSaving(false);
      showToast(`Chart saved to ${name}`);
      trackEvent('Insights - Chart Saved');
      navigate(route);
      return true;
    },
    [navigate, showToast, trackEvent],
  );

  const dashboardRoute = useCallback(
    (id: string) =>
      buildDashboardShow({ id, isEditing: true, portal: selectedPortal }),
    [selectedPortal],
  );

  const reportRoute = useCallback(
    (id: string) =>
      buildReportShow({ id, isEditing: true, portal: selectedPortal }),
    [selectedPortal],
  );

  const saveToExistingDashboard = useCallback(
    async (dashboard: DashboardType) => {
      setIsSaving(true);
      try {
        const now = DateTime.utc().toISO();
        const gadget = buildGadget(now);
        await createDashboardGadget(gadget, selectedAccountId);

        const card: CanvasCard.DashboardGadgetCard = {
          layout: buildCardLayout(
            0,
            findStartingY(dashboard.canvas.cards, 'desktop'),
          ),
          content: { type: 'Dashboard Gadget', dashboardGadgetId: gadget.id },
        };
        const newDashboard: DashboardType = {
          ...dashboard,
          canvas: {
            ...dashboard.canvas,
            cards: [...dashboard.canvas.cards, card],
          },
          updatedOn: now,
          updatedBy: currentUserId,
        };
        await updateDashboard({ id: dashboard.id, newDashboard, accountRef });
        addOrUpdateDashboard(newDashboard);
        return finish({
          name: dashboard.name,
          route: dashboardRoute(dashboard.id),
        });
      } catch {
        setIsSaving(false);
        showToast('Could not save the chart. Please try again.');
        return false;
      }
    },
    [
      buildGadget,
      selectedAccountId,
      currentUserId,
      accountRef,
      addOrUpdateDashboard,
      finish,
      dashboardRoute,
      showToast,
    ],
  );

  const saveToNewDashboard = useCallback(
    async ({ name, access }: { name: string; access: ResourceAccess }) => {
      setIsSaving(true);
      try {
        const now = DateTime.utc().toISO();
        const gadget = buildGadget(now);
        await createDashboardGadget(gadget, selectedAccountId);

        const card: CanvasCard.DashboardGadgetCard = {
          layout: buildCardLayout(0, 0),
          content: { type: 'Dashboard Gadget', dashboardGadgetId: gadget.id },
        };
        const newDashboard: DashboardType = {
          version: '5',
          id: getIdentifier(),
          name,
          canvas: { cards: [card] },
          isTemplate: false,
          variableDrillDowns: [],
          access,
          isV3Published: false,
          v3Access: { isPublic: false, groupIds: [], userIds: [] },
          labelIds: [],
          isAIGenerated: true,
          createdBy: currentUserId,
          createdOn: now,
          updatedBy: currentUserId,
          updatedOn: now,
        };
        await createDashboardAlt({ dashboard: newDashboard, accountRef });
        addOrUpdateDashboard(newDashboard);
        return finish({ name, route: dashboardRoute(newDashboard.id) });
      } catch {
        setIsSaving(false);
        showToast('Could not save the chart. Please try again.');
        return false;
      }
    },
    [
      buildGadget,
      selectedAccountId,
      currentUserId,
      accountRef,
      addOrUpdateDashboard,
      finish,
      dashboardRoute,
      showToast,
    ],
  );

  const saveToExistingReport = useCallback(
    async (report: ReportType) => {
      setIsSaving(true);
      try {
        const now = DateTime.utc().toISO();
        const gadget = buildGadget(now);
        const reportGadget: ReportGadgets.ReportGadget = {
          id: gadget.id,
          chartDef: gadget.chartDef,
          // ReportGadget has no drillDowns field, so an explored chart's
          // drill-downs have to ride along in scope or they're silently lost.
          scope: [...(gadget.scope ?? []), ...gadget.drillDowns],
          dateField: gadget.dateField,
          dateRange: gadget.dateRange,
          relativeDateRange: gadget.relativeDateRange,
          advancedRelativeDateRange: gadget.advancedRelativeDateRange,
          isAIGenerated: true,
          createdBy: currentUserId,
          createdOn: now,
          updatedBy: currentUserId,
          updatedOn: now,
        };
        await STORE.visualisations
          .getReportGadgetsRef({ accountId: selectedAccountId })
          .doc(reportGadget.id)
          .set(reportGadget);

        const card: CanvasCard.ReportGadgetCard = {
          layout: buildCardLayout(
            0,
            findStartingY(report.canvas.cards, 'desktop'),
          ),
          content: { type: 'Report Gadget', reportGadgetId: reportGadget.id },
        };
        const newReport: ReportType = {
          ...report,
          canvas: { ...report.canvas, cards: [...report.canvas.cards, card] },
          updatedOn: now,
          updatedBy: currentUserId,
        };
        await STORE.getReportsRef({ accountId: selectedAccountId })
          .doc(report.id)
          .set(withoutNulls(newReport));
        return finish({ name: report.name, route: reportRoute(report.id) });
      } catch {
        setIsSaving(false);
        showToast('Could not save the chart. Please try again.');
        return false;
      }
    },
    [
      buildGadget,
      selectedAccountId,
      currentUserId,
      finish,
      reportRoute,
      showToast,
    ],
  );

  const saveToNewReport = useCallback(
    async ({ name, access }: { name: string; access: ResourceAccess }) => {
      setIsSaving(true);
      try {
        const now = DateTime.utc().toISO();
        const gadget = buildGadget(now);
        const reportGadget: ReportGadgets.ReportGadget = {
          id: gadget.id,
          chartDef: gadget.chartDef,
          // ReportGadget has no drillDowns field, so an explored chart's
          // drill-downs have to ride along in scope or they're silently lost.
          scope: [...(gadget.scope ?? []), ...gadget.drillDowns],
          dateField: gadget.dateField,
          dateRange: gadget.dateRange,
          relativeDateRange: gadget.relativeDateRange,
          advancedRelativeDateRange: gadget.advancedRelativeDateRange,
          isAIGenerated: true,
          createdBy: currentUserId,
          createdOn: now,
          updatedBy: currentUserId,
          updatedOn: now,
        };
        await STORE.visualisations
          .getReportGadgetsRef({ accountId: selectedAccountId })
          .doc(reportGadget.id)
          .set(reportGadget);

        const card: CanvasCard.ReportGadgetCard = {
          layout: buildCardLayout(0, 0),
          content: { type: 'Report Gadget', reportGadgetId: reportGadget.id },
        };
        const newReport: ReportType = {
          version: '7',
          id: getIdentifier(),
          name,
          scope: [],
          drillDowns: [],
          canvas: { cards: [card] },
          dateField: 'date',
          access,
          isV3Published: false,
          v3Access: { isPublic: false, groupIds: [], userIds: [] },
          labelIds: [],
          isAIGenerated: true,
          createdBy: currentUserId,
          createdOn: now,
          updatedBy: currentUserId,
          updatedOn: now,
        };
        await STORE.getReportsRef({ accountId: selectedAccountId })
          .doc(newReport.id)
          .set(withoutNulls(newReport));
        return finish({ name, route: reportRoute(newReport.id) });
      } catch {
        setIsSaving(false);
        showToast('Could not save the chart. Please try again.');
        return false;
      }
    },
    [
      buildGadget,
      selectedAccountId,
      currentUserId,
      finish,
      reportRoute,
      showToast,
    ],
  );

  return {
    isSaving,
    saveToNewDashboard,
    saveToExistingDashboard,
    saveToNewReport,
    saveToExistingReport,
  };
};

export default useSaveInsightsChart;
